import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const UpgradesSection = ({ upgrades, onAddToCart, cartItems }) => {
  if (!upgrades || upgrades?.length === 0) return null;

  const getCartQuantity = (upgradeId) => {
    const cartItem = cartItems?.find(item => item?.id === upgradeId && item?.type === 'upgrade');
    return cartItem ? cartItem?.quantity : 0;
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    })?.format(price);
  };

  return (
    <div className="bg-card rounded-lg p-6 mb-8">
      <div className="flex items-center gap-2 mb-6">
        <Icon name="TrendingUp" size={24} className="text-accent" />
        <h2 className="text-2xl font-semibold text-foreground">Upgrades & Add-ons</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {upgrades?.map((upgrade) => {
          const cartQuantity = getCartQuantity(upgrade?.id);
          const isAvailable = upgrade?.available > upgrade?.sold;
          
          return (
            <div
              key={upgrade?.id}
              className={`border rounded-lg p-4 transition-colors ${
                !isAvailable 
                  ? 'border-border bg-muted/50' :'border-border hover:border-primary/50 bg-background'
              }`}
            >
              <div className="flex items-start gap-3 mb-3">
                <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name={upgrade?.icon || "Star"} size={20} className="text-accent" />
                </div>
                
                <div className="flex-1">
                  <h3 className={`font-medium mb-1 ${
                    !isAvailable ? 'text-muted-foreground' : 'text-foreground'
                  }`}>
                    {upgrade?.name}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-2">
                    {upgrade?.description}
                  </p>
                  
                  {upgrade?.features && upgrade?.features?.length > 0 && (
                    <div className="space-y-1 mb-3">
                      {upgrade?.features?.map((feature, index) => (
                        <div key={index} className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Icon name="Check" size={12} className="text-success" />
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <span className={`text-lg font-bold ${
                        !isAvailable ? 'text-muted-foreground' : 'text-foreground'
                      }`}>
                        {formatPrice(upgrade?.price)}
                      </span>
                      {!isAvailable && (
                        <p className="text-xs text-error mt-1">Sold Out</p>
                      )}
                      {isAvailable && upgrade?.available - upgrade?.sold <= 5 && (
                        <p className="text-xs text-warning mt-1">
                          Only {upgrade?.available - upgrade?.sold} left
                        </p>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {cartQuantity > 0 && (
                        <div className="flex items-center gap-2 px-2 py-1 bg-accent/10 rounded-lg">
                          <Button
                            variant="ghost"
                            size="xs"
                            iconName="Minus"
                            onClick={() => onAddToCart(upgrade, -1)}
                            className="w-6 h-6 p-0"
                          />
                          <span className="font-medium text-accent min-w-[16px] text-center text-sm">
                            {cartQuantity}
                          </span>
                          <Button
                            variant="ghost"
                            size="xs"
                            iconName="Plus"
                            onClick={() => onAddToCart(upgrade, 1)}
                            className="w-6 h-6 p-0"
                            disabled={cartQuantity >= 4}
                          />
                        </div>
                      )}
                      
                      {cartQuantity === 0 && (
                        <Button
                          variant={!isAvailable ? "ghost" : "outline"}
                          size="sm"
                          onClick={() => onAddToCart(upgrade, 1)}
                          disabled={!isAvailable}
                          iconName="Plus"
                        >
                          Add
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-6 pt-6 border-t border-border">
        <div className="flex items-start gap-2 text-sm text-muted-foreground">
          <Icon name="Info" size={16} className="mt-0.5 flex-shrink-0" />
          <p>
            Upgrades can be added to any ticket tier. Some upgrades may have limited availability 
            and are subject to venue capacity restrictions.
          </p>
        </div>
      </div>
    </div>
  );
};

export default UpgradesSection;